import { useEffect, useRef } from 'react';
import {
  Anchor,
  Banner,
  Box,
  Button,
  ButtonGroup,
  Card,
  CloseButton,
  Heading,
  Icon,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';

import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import * as S from './PaymentReportingCard.styles';
import * as rentReportingConstants from '@renter-hub/constants/rentReportingConstants';
import type { PaymentReportingInterface } from '@renter-hub/models/Types';
import { isTokenValid } from '../../../../utils/creditTrackingUtils';
import { IconArrowRightOutline } from '@zillow/constellation-icons';
import upsellIconSvg from '@assets/upsell-icon.svg';

type Props = {
  goToPaymentReportingPage: () => void;
  goToRentReportingPage: () => void;
  paymentReportingStatus: PaymentReportingInterface;
  requestPaymentReportingOptOut: (rentTermsKey: string) => { error: boolean };
  rentTermsKey: any;
  user: any;
  listingAlias: string;
  onOptOutClick: () => void;
  optOutError: boolean;
  setCurrentTab?: (tabIndex: number) => void;
  fetchCreditTrackingStatus: (rentTermsKey: string) => void;
  enableCreditTracking: boolean;
};

export const PaymentReportingCard = (props: Props) => {
  const { optedIn, optInDate, creditTracking } = props.paymentReportingStatus || { optedIn: false, optInDate: '' };
  const hasAttemptedRefetch = useRef(false);

  const { user, listingAlias, enableCreditTracking } = props;

  useEffect(() => {
    const isTokenInvalid = creditTracking && !isTokenValid(creditTracking);

    if (optedIn && isTokenInvalid && props.rentTermsKey && !hasAttemptedRefetch.current) {
      hasAttemptedRefetch.current = true;
      props.fetchCreditTrackingStatus(props.rentTermsKey);
    }
  }, [optedIn, props.rentTermsKey, creditTracking?.lastFetchedTimestamp]);

  const isEnrolledInCreditTracking = creditTracking?.arrayUserToken && isTokenValid(creditTracking);
  const isUnenrolledInCreditTracking =
    !creditTracking?.arrayUserToken && creditTracking?.enrollmentStatus === 'OPTED_OUT';
  const isNotEnrolledInCreditTracking =
    !creditTracking?.arrayUserToken &&
    (!creditTracking?.enrollmentStatus || creditTracking?.enrollmentStatus === 'NOT_ENROLLED');

  const title = optedIn ? `You're building credit` : `Want to build credit?`;
  const getSubTitle = () => {
    // opted in for rent reporting, not credit tracking
    if (optedIn) {
      const optInDateAsDate = new Date(optInDate);
      const month = optInDateAsDate?.toLocaleString('default', { month: 'long' });
      const year = optInDateAsDate?.getFullYear();
      return (
        <Text>
          You&apos;ve been enrolled in rent reporting since{' '}
          <strong>
            {month} {year}
          </strong>
          .
        </Text>
      );
    }
    return (
      <Text>Send your on-time rent payment history to Experian, Equifax, and TransUnion to build your credit</Text>
    );
  };

  const getLegacyHeader = () => (
    <Box>
      <S.CardHeader>
        <S.CardTitle>
          <Heading className="pvxs" textStyle="heading-xs" level={3}>
            {title}
          </Heading>
          {!optedIn && <Tag tone="aqua">Free</Tag>}
        </S.CardTitle>
        {getSubTitle()}
      </S.CardHeader>
    </Box>
  );

  const getStartedClick = () => {
    RachelCSEvents.emitOptIntoRentReportingDashboardEvent({ user, currentProperty: { listingAlias } });
    props.goToPaymentReportingPage();
    if (props.setCurrentTab) {
      props.setCurrentTab(1);
    }
  };

  const startTrackingCreditClick = () => {
    RachelCSEvents.emitStartCreditTrackingFromOverviewEvent();
    props.goToRentReportingPage();
    if (props.setCurrentTab) {
      props.setCurrentTab(1);
    }
  };

  const onViewMoreFAQClick = () => {
    RachelCSEvents.emitOpenRentReportingFAQDashboardEvent({ user, currentProperty: { listingAlias } });
  };

  const handleClickToReportingTab = () => {
    props.goToRentReportingPage();
    if (props.setCurrentTab) {
      props.setCurrentTab(1);
    }
  };

  const renderContent = () => {
    if (!optedIn) {
      if (!enableCreditTracking) {
        return (
          <>
            {getLegacyHeader()}
            <Box>
              <Button fluid={true} onClick={getStartedClick}>
                Get started
              </Button>
            </Box>
          </>
        );
      } else {
        return (
          <Box
            css={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: { base: 'flex-start', md: 'center' },
              padding: 'token(spacing.sm) !important',
              minHeight: 0,
              width: '100%',
              gap: { base: 12, md: 16 },
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', minWidth: 40 }}>
              <img
                src={upsellIconSvg as unknown as string}
                alt="Build credit"
                style={{ width: 32, height: 32, flexShrink: 0 }}
              />
            </Box>
            <Box
              css={{
                display: 'flex',
                flexDirection: { base: 'column', md: 'row' },
                alignItems: { base: 'flex-start', md: 'center' },
                flex: 1,
                gap: { base: 0, md: 16 },
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                <Box css={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <Heading textStyle="heading-xs" level={3} style={{ margin: 0 }}>
                    Want to build credit?
                  </Heading>
                  <Tag tone="aqua">Free</Tag>
                </Box>
                <Text css={{ marginBottom: 0, marginTop: 0 }}>
                  Send your on-time rent payment history to Experian, Equifax, and TransUnion to build your credit
                </Text>
              </Box>
              <Box
                css={{
                  display: 'flex',
                  alignItems: { base: 'flex-start', md: 'center' },
                  justifyContent: { base: 'flex-start', md: 'flex-end' },
                  width: { base: '100%', md: 'auto' },
                  marginTop: { base: 16, md: 0 },
                }}
              >
                <Button
                  emphasis="filled"
                  fluid={false}
                  onClick={getStartedClick}
                  css={{ fontWeight: 700, minWidth: 120 }}
                  tone="brand"
                >
                  Get started
                </Button>
              </Box>
            </Box>
          </Box>
        );
      }
    }
    if (optedIn && !enableCreditTracking) {
      return (
        <>
          {getLegacyHeader()}
          <ButtonGroup aria-label="faqs-opt-out" role="group" orientation="vertical">
            <Button onClick={onViewMoreFAQClick} fluid={true} asChild>
              <Anchor href={rentReportingConstants.CREDIT_REPORTING_FAQ_URL} target="_blank">
                View FAQs
              </Anchor>
            </Button>

            <TextButton textStyle="body-sm" onClick={props.onOptOutClick}>
              Opt out
            </TextButton>
          </ButtonGroup>
        </>
      );
    }

    if (optedIn && isNotEnrolledInCreditTracking) {
      return (
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <Box css={{ display: 'flex', alignItems: 'center', marginBottom: 'token(spacing.sm)' }}>
            <Heading level={5}>Rent reporting</Heading>
          </Box>
          <Box css={{ display: 'flex', alignItems: 'flex-start', marginBottom: 'token(spacing.xs)' }}>
            <Text css={{ flex: 1 }}>You can now track your credit score directly on your payments dashboard</Text>
          </Box>
          <Box css={{ marginTop: 'token(spacing.sm)' }}>
            <TextButton onClick={startTrackingCreditClick} textStyle="body-bold" tone="brand">
              Start tracking your credit today
              <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 12 }}>
                <Tag tone="aqua" size="sm" css={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                  NEW
                </Tag>
              </span>
            </TextButton>
          </Box>
        </Box>
      );
    }

    if (optedIn && (!creditTracking || isUnenrolledInCreditTracking)) {
      // Fallback: show isUnenrolled view if we can't fetch credit tracking status or user is unenrolled
      return (
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <Box css={{ display: 'flex', alignItems: 'center', marginBottom: 'token(spacing.sm)' }}>
            <Heading level={5}>Rent reporting</Heading>
          </Box>
          {getSubTitle()}
          <Box css={{ marginTop: 'token(spacing.sm)' }}>
            <TextButton onClick={handleClickToReportingTab} textStyle="body-bold" tone="brand">
              See more
              <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>
                <Icon render={<IconArrowRightOutline />} color="blue" size="md" />
              </span>
            </TextButton>
          </Box>
        </Box>
      );
    }

    if (optedIn && isEnrolledInCreditTracking) {
      return (
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <Box css={{ display: 'flex', alignItems: 'center', marginBottom: 'token(spacing.sm)' }}>
            <Heading level={5}>Rent reporting</Heading>
          </Box>
          {getSubTitle()}
          <Box css={{ marginTop: 'token(spacing.sm)' }}>
            <TextButton onClick={handleClickToReportingTab} textStyle="body-bold" tone="brand">
              See your latest credit score
              <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>
                <Icon render={<IconArrowRightOutline />} color="blue" size="md" />
              </span>
            </TextButton>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Card
      css={{
        padding: enableCreditTracking ? '' : 'token(spacing.sm) !important',
        marginBottom: 'token(spacing.md) !important',
      }}
      elevated={false}
    >
      {props.optOutError && (
        <Banner
          tone="warning"
          closeButton={<CloseButton onImpact />}
          css={{ marginBottom: 'token(spacing.sm) !important' }}
        >
          There was an issue opting out of rent payment reporting. Please try again.
        </Banner>
      )}
      <Box
        css={{
          display: enableCreditTracking ? 'flex' : 'grid',
          gridTemplateColumns: { base: '1fr', md: '7fr 5fr' },
          gap: '16px',
        }}
      >
        {renderContent()}
      </Box>
    </Card>
  );
};
